import {
  type GetSessionPresentersArgs,
  type SessionsPresenter,
  useInfiniteScrollFromCache,
  useLazyGetSessionsPresentersQuery,
} from '@carboncopyco/data';
import {
  Avatar,
  type DropdownFilterItemContent,
  TextBase,
  stringToColor,
} from '@carboncopyco/ui';
import { getTimezoneName } from '@carboncopyco/utils';
import { useEffect, useMemo } from 'react';
import { checkboxClasses } from '../../Dashboard/DashboardHeader/WorkspaceFilter';
import { SearchDropdown } from '../../SearchDropdown';
import { useUrlState } from '../../utils';
import type { UrlState } from '../types';

type PresentersFilterProps = {
  organizationId: string;
  workspaceId: string;
} & (
  | { playbookId?: never; demoId: string }
  | { playbookId: string; demoId?: never }
);

export const PresentersFilter = ({
  organizationId,
  workspaceId,
  demoId,
  playbookId,
}: PresentersFilterProps) => {
  const [urlState, setUrlState] = useUrlState<UrlState>({});
  const presenters = urlState.presenters;
  const fetchArgs = useMemo<GetSessionPresentersArgs>(
    () => ({
      organizationId,
      shortSessions: true,
      internalSessions: true,
      workspaceIds: [workspaceId],
      timezone: getTimezoneName(),
      resourceTypes: playbookId ? ['play'] : ['managed_presentation'],
      ...(demoId ? { demoId } : { playbookId }),
    }),
    [organizationId, workspaceId, playbookId, demoId]
  );

  const {
    items,
    isLoading,
    isLoadingBatch,
    fetchFirstBatch,
    fetchNextBatch,
    isEmpty,
  } = useInfiniteScrollFromCache<SessionsPresenter, GetSessionPresentersArgs>(
    useLazyGetSessionsPresentersQuery,
    fetchArgs
  );

  useEffect(() => {
    fetchFirstBatch();
  }, [fetchArgs]);

  const filterItems: DropdownFilterItemContent<string>[] =
    items?.map((item) => ({
      ...item,
      id: item.email,
      label: item.name,
    })) ?? [];

  const selectedItems = presenters
    ? Object.fromEntries(
        presenters.split(',').map((presenterId) => {
          return [
            presenterId,
            filterItems.find((presenter) => presenter.id === presenterId)
              ?.label ?? '',
          ];
        })
      )
    : {};

  const onChange = (optionId) => {
    const optionsSet = new Set(
      presenters?.split(',').map((option) => option.trim())
    );
    if (optionsSet.has(optionId)) {
      optionsSet.delete(optionId);
    } else {
      optionsSet.add(optionId);
    }
    setUrlState({
      ...urlState,
      presenters: Array.from(optionsSet.values()).join(','),
    });
  };

  const renderPresenterItem = ({ checked, onChange, item }) => {
    return (
      <div className="px-2 h-9 cursor-pointer flex items-center w-full gap-2 focus:bg-ds-natural-50 focus:outline-none focus:rounded-md">
        <div className="flex items-center gap-3">
          <input
            checked={checked}
            onChange={onChange}
            type="checkbox"
            className={checkboxClasses(checked)}
          />
          <div className="flex items-center gap-2 truncate">
            <Avatar
              backgroundColor={stringToColor(item.label)}
              image={item.image}
              size="small"
              initials={item.label
                .split(' ')
                .map((word) => word[0])
                .join('')}
            />
            <div className="truncate max-w-[120px]">
              <TextBase
                color="text-ds-natural-600"
                label={item.label}
                ellipsis
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SearchDropdown
      placeholder="Presenter"
      capsuleWidth={208}
      contentWidth={208}
      fixedItems={filterItems}
      selectedItems={selectedItems}
      onChange={onChange}
      onClear={() => setUrlState({ ...urlState, presenters: undefined })}
      onInputChange={fetchFirstBatch}
      fetchNextBatch={fetchNextBatch}
      isLoading={isLoading}
      isLoadingBatch={isLoadingBatch}
      renderItem={renderPresenterItem}
      disabled={isEmpty}
    />
  );
};
